import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import * as actionTypes from "./actionTypes";
import {
  getAllTrainingDefinitionAPI,
  getSpecificTrainingDefinitionAPI,
  createTrainingDefinitionAPI,
  updateTrainingDefinitionAPI,
  deleteTrainingDefinitionAPI,
  duplicateTrainingDefinitionAPI,
  getTrainingDefinitionModulesAPI,
  createTrainingDefinitionModuleAPI,
  updateTrainingDefinitionModuleAPI,
  deleteTrainingDefinitionModuleAPI,
  getTrainingDefinitionActivityAPI,
  createTrainingDefinitionActivityAPI,
  updateTrainingDefinitionActivityAPI,
  deleteTrainingDefinitionActivityAPI,
  getTrainingDefinitionActivitiesAPI,
  getTrainingDefinitionAssignmentAPI,
  createTrainingDefinitionAssignmentAPI,
  deleteTrainingDefinitionAssignmentAPI,
  getTrainingTreeTrainingDefinitionAPI,
} from "@icarius-connection/api";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";

const errorHandler = (e, dispatch, variant) => {
  let errorString = (() => {
    switch (e.response?.data?.status) {
      case "IN_USE":
        if (variant === "module") return getLocalizedString("moduleInUse");
        if (variant === "activity") return getLocalizedString("activityInUse");
        return "";
      case "DUPLICATED_DATA":
        if (variant === "training") return getLocalizedString("trainingDuplicatedData");
        if (variant === "module") return getLocalizedString("moduleDuplicatedData");
        if (variant === "activity") return getLocalizedString("activityDuplicatedData");
        return "";
      case "ASSIGNED_AS_PARTICIPANT": return getLocalizedString("cannotAssignParticipantAsInstructor");
      case "INCOMPLETE_TRAINING": return getLocalizedString("trainingPublishedRequiredFields");
      case "INCOMPLETE_ACTIVITIES": return getLocalizedString("trainingPublishedActivityRequiredFields");
      case "INVALID_DATA":
      case "VALIDATION_ERROR": return e.response?.data?.error === "INVALID_EXTENSION" ? getLocalizedString("invalidFileExtension") : getLocalizedString("validationError");
      default: return getErrorStringFromServerError(e);
    }
  })();

  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

// TRAINING ///////////////////////////////////////////////////////////////////////////

export const getAllTrainingDefinitionAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ALL_TRAINING });
  try {
    const response = await getAllTrainingDefinitionAPI();

    const trainingList = response.data?.trainingList;
    const courseList = response.data?.courseList;
    const skillList = response.data?.skillList;
    const collaboratorsList = response.data?.collaboratorList;
    const instructorList = response.data?.instructorList;
    const userFieldsDefinition = response.data?.userFieldsDefinition;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ALL_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ALL_TRAINING_FULFILLED,
      payload: {
        trainingList,
        courseList,
        skillList,
        collaboratorsList,
        instructorList,
        userFieldsDefinition,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ALL_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const getSpecificTrainingDefinitionAction = (trainingId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_SPECIFIC_TRAINING });
  try {
    const response = await getSpecificTrainingDefinitionAPI(trainingId);

    const training = response.data?.training;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_SPECIFIC_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_SPECIFIC_TRAINING_FULFILLED,
      payload: {
        training,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_SPECIFIC_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const getTrainingTreeTrainingDefinitionAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_COURSE_TREE });
  try {
    let response = await getTrainingTreeTrainingDefinitionAPI(courseId);

    let trainingTree = response.data?.data;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_COURSE_TREE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_COURSE_TREE_FULFILLED,
      payload: {
        trainingTree,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_COURSE_TREE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const createTrainingDefinitionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_TRAINING });
  try {
    const response = await createTrainingDefinitionAPI(dataToSend);

    const trainingList = response.data?.trainingList;
    const courseList = response.data?.courseList;
    const instructorList = response.data?.instructorList;
    const userFieldsDefinition = response.data?.userFieldsDefinition;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_TRAINING_FULFILLED,
      payload: {
        trainingList,
        courseList,
        instructorList,
        userFieldsDefinition,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const updateTrainingDefinitionAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_TRAINING });
  try {
    const response = await updateTrainingDefinitionAPI(trainingId, dataToSend);

    const trainingList = response.data?.trainingList;
    const courseList = response.data?.courseList;
    const instructorList = response.data?.instructorList;
    const userFieldsDefinition = response.data?.userFieldsDefinition;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_TRAINING_FULFILLED,
      payload: {
        trainingList,
        courseList,
        instructorList,
        userFieldsDefinition,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const deleteTrainingDefinitionAction = (trainingId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_TRAINING });
  try {
    const response = await deleteTrainingDefinitionAPI(trainingId);

    const trainingList = response.data?.trainingList;
    const courseList = response.data?.courseList;
    const instructorList = response.data?.instructorList;
    const userFieldsDefinition = response.data?.userFieldsDefinition;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_TRAINING_FULFILLED,
      payload: {
        trainingList,
        courseList,
        instructorList,
        userFieldsDefinition,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const duplicateTrainingDefinitionAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DUPLICATE_TRAINING });
  try {
    const response = await duplicateTrainingDefinitionAPI(trainingId, dataToSend);

    const trainingList = response.data?.trainingList;
    const courseList = response.data?.courseList;
    const instructorList = response.data?.instructorList;
    const userFieldsDefinition = response.data?.userFieldsDefinition;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DUPLICATE_TRAINING_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DUPLICATE_TRAINING_FULFILLED,
      payload: {
        trainingList,
        courseList,
        instructorList,
        userFieldsDefinition,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseDuplicatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DUPLICATE_TRAINING_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

// MODULES ///////////////////////////////////////////////////////////////////////////

export const getTrainingDefinitionModulesAction = (trainingId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_MODULES });
  try {
    const response = await getTrainingDefinitionModulesAPI(trainingId);

    const modules = response.data?.modules;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_MODULES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_MODULES_FULFILLED,
      payload: {
        modules,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_MODULES_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const createTrainingDefinitionModuleAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_MODULE });
  try {
    const response = await createTrainingDefinitionModuleAPI(trainingId, dataToSend);

    const modules = response.data?.modules;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const updateTrainingDefinitionModuleAction = (trainingId, moduleId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_MODULE });
  try {
    const response = await updateTrainingDefinitionModuleAPI(trainingId, moduleId, dataToSend);

    const modules = response.data?.modules;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const deleteTrainingDefinitionModuleAction = (trainingId, moduleId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_MODULE });
  try {
    const response = await deleteTrainingDefinitionModuleAPI(trainingId, moduleId);

    const modules = response.data?.modules;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

// ACTIVITIES ///////////////////////////////////////////////////////////////////////////

export const getTrainingDefinitionActivitiesAction = (trainingId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ACTIVITIES });
  try {
    const response = await getTrainingDefinitionActivitiesAPI(trainingId);

    const activities = response.data?.activities;
    const media = response.data?.media;
    const activityTypes = response.data?.activityTypes;
    const questionTypes = response.data?.questionTypes;
    const sessionTypes = response.data?.sessionTypes;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ACTIVITIES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ACTIVITIES_FULFILLED,
      payload: {
        activities,
        media,
        activityTypes,
        questionTypes,
        sessionTypes,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ACTIVITIES_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const getTrainingDefinitionActivityAction = (trainingId, activityId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ACTIVITY });
  try {
    const response = await getTrainingDefinitionActivityAPI(trainingId, activityId);

    const activity = response.data?.activity;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ACTIVITY_FULFILLED,
      payload: {
        activity,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const createTrainingDefinitionActivityAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_ACTIVITY });
  try {
    const response = await createTrainingDefinitionActivityAPI(trainingId, dataToSend);

    const activities = response.data?.activities;
    const media = response.data?.media;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const updateTrainingDefinitionActivityAction = (trainingId, activityId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_ACTIVITY });
  try {
    const response = await updateTrainingDefinitionActivityAPI(trainingId, activityId, dataToSend);

    const activities = response.data?.activities;
    const media = response.data?.media;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const deleteTrainingDefinitionActivityAction = (trainingId, activityId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_ACTIVITY });
  try {
    const response = await deleteTrainingDefinitionActivityAPI(trainingId, activityId);

    const activities = response.data?.activities;
    const media = response.data?.media;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};


// ASSIGNMENT ////////////////////////////////////////////////////////////////////////////

export const getTrainingDefinitionAssignmentAction = (trainingId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_TRAINING_ASSIGNMENT });
  try {
    const response = await getTrainingDefinitionAssignmentAPI(trainingId);

    const canAssign = response.data?.canAssign;
    const assignedCollaborators = response.data?.assignedCollaborators;
    const collaboratorsList = response.data?.collaboratorsList;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_TRAINING_ASSIGNMENT_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_TRAINING_ASSIGNMENT_FULFILLED,
      payload: {
        canAssign,
        assignedCollaborators,
        collaboratorsList,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_TRAINING_ASSIGNMENT_REJECTED, payload: e });
    errorHandler(e, dispatch, "training");
    return e.response;
  }
};

export const createTrainingDefinitionAssignmentAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_TRAINING_ASSIGNMENT });
  try {
    const response = await createTrainingDefinitionAssignmentAPI(trainingId, dataToSend);

    const assignedCollaborators = response.data?.assignedCollaborators;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_TRAINING_ASSIGNMENT_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_TRAINING_ASSIGNMENT_FULFILLED,
      payload: {
        assignedCollaborators,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_TRAINING_ASSIGNMENT_REJECTED, payload: e });
    errorHandler(e, dispatch, "");
    return e.response;
  }
};

export const deleteTrainingDefinitionAssignmentAction = (trainingId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_TRAINING_ASSIGNMENT });
  try {
    const response = await deleteTrainingDefinitionAssignmentAPI(trainingId, dataToSend);

    const assignedCollaborators = response.data?.assignedCollaborators;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_TRAINING_ASSIGNMENT_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_TRAINING_ASSIGNMENT_FULFILLED,
      payload: {
        assignedCollaborators,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_TRAINING_ASSIGNMENT_REJECTED, payload: e });
    errorHandler(e, dispatch, "");
    return e.response;
  }
};